import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useStreamedText, sliceStream } from '../hooks/useStreamedText';
import { smoothScrollToElement } from '../utils/smoothScroll';
import { askQimen, QimenChatMessage, QimenReadingData } from '../services/qimenAiService';
import { crossReading } from '../features/qimen/qimen/analysis.js';

/* ============================================================
   奇门 AI 求测与解读（集成组件，位于九宫盘面之下）
   - 「大师解读」：定用神/求测人宫/事体宫/九宫要点/格局/应期/建议
   - 自由提问（带问题类别）与追问：盘面数据每轮随请求发送（服务端固化）
   - 用神规则：日干＝求测人，时干＝所测之事（由服务端提示词强制执行）
   缓存：Firestore qimenSessions ＋ localStorage 按局身份（solarText|盘法）缓存
   ============================================================ */

const CACHE_KEY = 'qm-ai-cache-v1';
const CACHE_LIMIT = 6;

// 快捷问法：key 与盘面「问事类别」一致，点一下即带入问题
const QIMEN_HINTS: Array<{ key: string; label: string; category: string; question: string }> = [
  { key: 'career', label: '事业求职', category: '事业求职', question: '最近求职/事业方向怎么看，何时动比较好？' },
  { key: 'wealth', label: '财运求财', category: '财运求财', question: '最近财运怎么样，适合投资或谈钱吗？' },
  { key: 'love', label: '感情婚姻', category: '感情婚姻', question: '说说我的感情婚姻现状与趋势。' },
  { key: 'health', label: '健康', category: '健康', question: '我的身体需要注意什么？' },
  { key: 'travel', label: '出行调动', category: '出行调动', question: '近期出行/调动顺利吗？走哪个方向好？' },
  { key: 'legal', label: '官司纠纷', category: '官司纠纷', question: '眼下的纠纷/竞争局势如何？' }
];

interface QimenAiCacheEntry {
  identity: string;
  sessionId: string | null;
  title: string;
  messages: QimenChatMessage[];
  updatedAt: number;
}

function loadAllCache(): QimenAiCacheEntry[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter(e => e && typeof e.identity === 'string' && Array.isArray(e.messages)) : [];
  } catch {
    return [];
  }
}

function saveCache(entry: QimenAiCacheEntry): void {
  try {
    const list = loadAllCache().filter(e => e.identity !== entry.identity);
    list.unshift(entry);
    localStorage.setItem(CACHE_KEY, JSON.stringify(list.slice(0, CACHE_LIMIT)));
  } catch {
    /* ignore */
  }
}

function chartIdentity(chart: any): string {
  if (!chart) return '';
  const input = chart.input || {};
  return [chart.solarText || '', input.shenMode || '', input.juMethod || ''].join('|');
}

/* ---------- 渲染辅助 ---------- */
function QimenTextLines({ text, caret = false }: { text: string; caret?: boolean }) {
  const lines = String(text || '').split('\n').filter(l => l.trim().length > 0);
  return (
    <>
      {lines.map((line, i) => (
        /^白话一句话[:：]/.test(line.trim())
          ? <div key={i} className="qm-ai-bubble-takeaway">
              {line}
              {caret && i === lines.length - 1 && <span className="stream-caret" aria-hidden />}
            </div>
          : <p key={i}>
              {line}
              {caret && i === lines.length - 1 && <span className="stream-caret" aria-hidden />}
            </p>
      ))}
    </>
  );
}

function stripHeader(text: string): string {
  return String(text || '').replace(/^【[^】]{1,14}】\s*/, '').trim();
}

/* ---------- 单条消息（聊天面板与历史详情共用） ---------- */
export const QimenAiMessageView: React.FC<{ msg: QimenChatMessage; animate?: boolean }> = ({ msg, animate = false }) => {
  if (msg.role === 'user') {
    return (
      <div className="qm-ai-msg qm-ai-msg-user">
        <div className="qm-ai-bubble">{typeof msg.content === 'string' ? msg.content : ''}</div>
      </div>
    );
  }
  return <QimenAssistantMessage msg={msg} animate={animate} />;
};

const QimenAssistantMessage: React.FC<{ msg: QimenChatMessage; animate: boolean }> = ({ msg, animate }) => {
  const data = (typeof msg.content === 'object' && msg.content ? msg.content : {}) as QimenReadingData;
  const sections: Array<[string, string | undefined]> = [
    ['定用神', data.yongShen],
    ['求测人宫 · 我况', data.selfPalace],
    ['事体宫 · 事况', data.matterPalace],
    ['九宫要点', data.palaceRead],
    ['吉凶格局', data.patterns],
    ['应期提示', data.timing],
    ['行动建议', data.advice]
  ];
  const visibleSections = sections.filter(([, v]) => typeof v === 'string' && v.length > 0);
  const reading = visibleSections.length > 0;
  const mainTexts = reading
    ? visibleSections.map(([, value]) => stripHeader(value || ''))
    : [data.analysis || ''];
  const summaryText = data.summary ? stripHeader(data.summary) : '';
  const streamTexts = summaryText ? [...mainTexts, summaryText] : mainTexts;
  const summaryIndex = summaryText ? streamTexts.length - 1 : -1;
  const stream = useStreamedText(streamTexts, animate);
  const { parts, active } = sliceStream(streamTexts, stream.visible);

  return (
    <div className="qm-ai-msg qm-ai-msg-bot">
      {typeof msg.error === 'string' && msg.error ? (
        <div className="qm-ai-bubble qm-ai-error">⚠ {msg.error}</div>
      ) : (
        <div className={`qm-ai-bubble ${animate && !stream.done ? 'is-streaming' : ''}`}>
          <div className="qm-ai-tip">日干＝求测人自己，时干＝所测之事（用神规则）</div>
          {reading ? (
            <div className="qm-ai-reading">
              {visibleSections.map(([name], index) => (
                <div key={name} className="qm-ai-block">
                  <b className="qm-ai-block-header">{`【${name}】`}</b>
                  <QimenTextLines
                    text={parts[index] || ''}
                    caret={animate && !stream.done && active === index}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="qm-ai-ask-reply">
              <QimenTextLines
                text={parts[0] || ''}
                caret={animate && !stream.done && active === 0}
              />
            </div>
          )}
          {summaryText && (
            <div className="qm-ai-bubble-takeaway">
              {reading ? '综括：' : '结论：'}
              {parts[summaryIndex] || ''}
              {animate && !stream.done && active === summaryIndex && (
                <span className="stream-caret" aria-hidden />
              )}
            </div>
          )}
          {data.disclaimer && (!animate || stream.done) && (
            <div className="qm-ai-disclaimer">{stripHeader(data.disclaimer)}</div>
          )}
          {animate && !stream.done && (
            <button type="button" className="stream-skip is-inline" onClick={stream.skip}>
              显示全部
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const QimenAiThreadView: React.FC<{
  messages: QimenChatMessage[];
  threadRef?: React.MutableRefObject<HTMLDivElement | null>;
  /** Timestamp of the assistant message that should stream in. */
  streamTs?: number | null;
}> = ({ messages, threadRef, streamTs = null }) => (
  <div className="qm-ai-thread" ref={threadRef}>
    {messages.length === 0 && <div className="qm-ai-empty">暂无对话。</div>}
    {messages.map((m, i) => (
      <QimenAiMessageView
        key={i}
        msg={m}
        animate={m.role === 'assistant' && m.ts === streamTs}
      />
    ))}
  </div>
);

/* ============================================================ */

export default function QimenAiChat({
  chart,
  analysis = null,
  onAsked
}: {
  chart: any;
  analysis?: any;
  /** 用户提问后回调：页面据此展示盘面关系判读（问什么断什么） */
  onAsked?: (payload: { question: string; category: string }) => void;
}) {
  const { user } = useAuth();
  const identity = useMemo(() => chartIdentity(chart), [chart]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionTitle, setSessionTitle] = useState('');
  const [messages, setMessages] = useState<QimenChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [activeCategory, setActiveCategory] = useState('综合');
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState('');
  const [restoredNote, setRestoredNote] = useState(false);


  const [confirmReset, setConfirmReset] = useState(false);
  const [streamTs, setStreamTs] = useState<number | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const userInteractedRef = useRef(false);

  useEffect(() => {
    if (!identity) return;
    const hit = loadAllCache().find(e => e.identity === identity);
    setSessionId(hit?.sessionId ?? null);
    setSessionTitle(hit?.title ?? '');
    setMessages(hit?.messages ?? []);
    setStreamTs(null);
    if (hit && hit.messages.length > 0) setRestoredNote(true);
  }, [identity]);

  const cacheTimer = useRef<number | null>(null);
  useEffect(() => {
    if (!identity || messages.length === 0) return;
    if (cacheTimer.current) window.clearTimeout(cacheTimer.current);
    cacheTimer.current = window.setTimeout(() => {
      saveCache({ identity, sessionId, title: sessionTitle, messages, updatedAt: Date.now() });
    }, 400);
    return () => {
      if (cacheTimer.current) window.clearTimeout(cacheTimer.current);
    };
  }, [identity, sessionId, sessionTitle, messages]);

  // 只有用户主动发起对话后才滚到聊天区；初次进入/恢复缓存时不抢盘面的滚动位置。
  useEffect(() => {
    if (!userInteractedRef.current) return;
    if (!threadRef.current) return;
    requestAnimationFrame(() => {
      if (threadRef.current) {
        smoothScrollToElement(threadRef.current, { align: 'end', padding: 24, duration: 520 });
      }
    });
  }, [messages.length, busy]);

  const categoryKeyOf = (label: string): string => QIMEN_HINTS.find((h) => h.category === label)?.key || 'general';

  // 发送时按当前所选类别即时计算交叉分析，保证 AI 拿到的用神/生克与所问一致
  const analysisFor = useCallback(
    (categoryLabel: string) => (chart ? crossReading(chart, categoryKeyOf(categoryLabel)) : null),
    [chart]
  );

  const runAsk = useCallback(async (mode: 'reading' | 'ask', question: string, category: string) => {
    if (busy || !chart || !identity) return;
    userInteractedRef.current = true;
    const shownText = mode === 'reading' ? '【大师解读】请用奇门解读当前盘。' : question;
    onAsked?.({ question: mode === 'reading' ? '此盘整体解读' : question, category: categoryKeyOf(category) });
    setBusy(true);
    setSendError('');
    setRestoredNote(false);
    setMessages(prev => [...prev, { role: 'user', content: shownText, mode, ts: Date.now() }]);
    try {
      const res = await askQimen(user, { mode, question, category, chart, analysis: analysisFor(category) || analysis, history: messages, sessionId });
      const ts = Date.now();
      setMessages(prev => [...prev, { role: 'assistant', content: res.data, mode, ts }]);
      setStreamTs(ts);
      if (res.sessionId) setSessionId(res.sessionId);
      if (res.title) setSessionTitle(res.title);
    } catch (err: any) {
      const status = err?.status;
      let text: string;
      if (status === 400) text = `请求被拒：${err?.message || '输入内容未通过服务端校验'}`;
      else if (status === 401 || status === 403) text = '登录状态已过期，请刷新页面后重试。';
      else if (status === 500) text = '解读服务还没配置模型或暂时出错，请稍后再试。';
      else if (status === 502) text = 'AI 服务返回了无效内容或暂时过载，请重试。';
      else if (status === 429) text = '请求太快了，休息一下再发。';
      else text = '解读服务器暂时无响应，请检查网络后重试。';
      setMessages(prev => [...prev, { role: 'assistant', content: '', mode, error: text, ts: Date.now() }]);
      setSendError(text);
    } finally {
      setBusy(false);
    }
  }, [busy, chart, identity, messages, sessionId, user, analysis, onAsked, analysisFor]);

  const submitQuestion = () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput('');
    void runAsk('ask', q, activeCategory);
  };

  const resetSession = () => {
    if (!confirmReset) {
      setConfirmReset(true);
      window.setTimeout(() => setConfirmReset(false), 4000);
      return;
    }
    setConfirmReset(false);
    setMessages([]);
    setSessionId(null);
    setSessionTitle('');
    setSendError('');
    setRestoredNote(false);
    setStreamTs(null);
    if (identity) saveCache({ identity, sessionId: null, title: '', messages: [], updatedAt: Date.now() });
  };

  if (!chart) return null;

  const hasMessages = messages.length > 0;

  return (
    <section className="qm-ai-section">
      <div className="qm-ai-head">
        <h2 className="qm-ai-title">奇门 AI 求测与解读</h2>
        <p className="qm-ai-sub">
          用神规则：日干为你，时干为你所测之事；大师解读按定用神、两宫合参、格局、应期、建议九节输出。盘面数据随每轮发送。
        </p>
      </div>

      {restoredNote && hasMessages && (
        <div className="qm-ai-restored">已恢复此局的求测记录，可继续追问。</div>
      )}

      <QimenAiThreadView messages={messages} threadRef={threadRef} streamTs={streamTs} />

      {busy && (
        <div className="qm-ai-msg qm-ai-msg-bot qm-ai-msg-typing">
          <div className="qm-ai-typing"><i /><i /><i /></div>
        </div>
      )}

      {sendError && !hasMessages && (
        <div className="qm-ai-msg qm-ai-msg-bot">
          <div className="qm-ai-bubble qm-ai-error">⚠ {sendError}</div>
        </div>
      )}

      {!busy && (
        <div className="qm-ai-composer">
          {!hasMessages && (
            <div className="qm-ai-entry">
              <button type="button" className="qm-ai-btn-primary" onClick={() => void runAsk('reading', '', activeCategory)}>
                大师解读
              </button>
            </div>
          )}
          <div className="qm-ai-hint">
            {QIMEN_HINTS.map(h => (
              <button
                key={h.label} type="button"
                className={`qm-ai-hint-chip${activeCategory === h.category ? ' qm-ai-hint-chip-on' : ''}`}
                onClick={() => { setActiveCategory(h.category); setInput(h.question); }}
              >
                {h.label}
              </button>
            ))}
          </div>
          <div className="qm-ai-ask">
            <textarea
              className="qm-ai-input"
              value={input}
              rows={hasMessages ? 1 : 2}
              maxLength={500}
              placeholder={hasMessages ? '继续追问…（同一奇门局）' : '想问什么？先选一个类别（金色高亮）再提问。'}
              disabled={busy}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submitQuestion();
                }
              }}
            />
            <button type="button" className="qm-ai-send" onClick={submitQuestion} disabled={!input.trim() || busy}>
              发送
            </button>
          </div>
          {hasMessages && (
            <div className="qm-ai-footer">
              <button type="button" className="qm-ai-reset" onClick={resetSession}>
                {confirmReset ? '再点一次确认清空' : '新会话'}
              </button>
              {sessionTitle && <span className="qm-ai-session-title">{sessionTitle}</span>}
            </div>
          )}
        </div>
      )}

      <p className="qm-ai-disclaimer">
        分析内容为传统数术文化的辅助推导，仅供参考，不构成医疗、投资或法律建议；身体与重大决策请以专业意见为准。
      </p>
    </section>
  );
}
